//path: src\common\Avatar.tsx

import { useAuthUser } from '@/hooks/useAuthUser';
import { getUserAvatar, getUserAvatarCrop, getUserInitial } from '@/lib/user-utils';
import { Avatar as RadixAvatar, AvatarFallback, AvatarImage as RadixAvatarImage } from '@/components/cms/ui/avatar';
import { AvatarImage } from '@/components/cms/ui/avatar-image';
import { useEffect, useRef, useState } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { StandardCropData } from '@/modules/uiImages/types';
import { AVATAR_CHANGED } from './avatarEvents';
import { shownUrl } from '@/utils/localCopies';

interface AvatarProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCrop?: boolean;
  avatarUrl?: string;
  refreshTrigger?: number; // Add this to force refresh when changed
  /** On the hero band: the initials sit in the band's own plum, so it reads the same in every theme. */
  onHero?: boolean;
}

export function Avatar({ className = '', size = 'md', showCrop = true, avatarUrl: propAvatarUrl, refreshTrigger, onHero = false }: AvatarProps) {
  const { user } = useAuthUser();
  const services = useServices();
  const [changes, setChanges] = useState(0);
  useEffect(() => {
    const onChanged = () => setChanges(n => n + 1);
    window.addEventListener(AVATAR_CHANGED, onChanged);
    return () => window.removeEventListener(AVATAR_CHANGED, onChanged);
  }, []);
  const [uiImageAvatarUrl, setUiImageAvatarUrl] = useState<string | null>(null);
  const [uiImageAvatarOriginalUrl, setUiImageAvatarOriginalUrl] = useState<string | null>(null);
  const [uiImageAvatarCroppedUrl, setUiImageAvatarCroppedUrl] = useState<string | null>(null);
  
  // Use prop URL if provided, otherwise use UI Images system, fallback to user metadata
  const avatarUrl = propAvatarUrl !== undefined ? propAvatarUrl : (uiImageAvatarUrl || getUserAvatar(user));
  const legacyCropData = getUserAvatarCrop(user);
  
  // Convert legacy crop data to standard format if needed
  const cropData: StandardCropData | undefined = legacyCropData ? {
    x: legacyCropData.x || 0,
    y: legacyCropData.y || 0,
    width: legacyCropData.width || 0,
    height: legacyCropData.height || 0
  } : undefined;
  
  // If we have a cropped image from UI Images, don't apply additional transforms
  const shouldApplyCrop = showCrop && cropData && !uiImageAvatarUrl;

  // Measure container width to convert normalized crop offsets to pixels
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Load avatar from UI Images system
  useEffect(() => {
    const loadAvatarFromUIImages = async () => {
      if (!user?.id || propAvatarUrl !== undefined) return; // Skip if prop URL provided

      try {
        const avatarImages = await services.uiImages.getUIImages({
          userId: user.id,
          imageCategory: 'Avatar'
        });

        if (!avatarImages.success) return;
        const avatarImage = avatarImages.data?.[0];
        // Store both URLs separately
        setUiImageAvatarOriginalUrl(avatarImage?.imageOriginalUrl || null);
        setUiImageAvatarCroppedUrl(avatarImage?.imageCroppedUrl || null);
        // Use cropped URL if available, otherwise original URL for display
        setUiImageAvatarUrl(avatarImage?.imageCroppedUrl || avatarImage?.imageOriginalUrl || null);
      } catch (error) {
        console.error('Error loading avatar from UI Images:', error);
      }
    };

    loadAvatarFromUIImages();
  }, [user?.id, propAvatarUrl, services.uiImages, refreshTrigger, changes]);
  
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10', 
    lg: 'h-12 w-12',
    xl: 'h-24 w-24'
  };

  if (!avatarUrl) {
    const initial = getUserInitial(user);
    return (
      <div className={`${sizeClasses[size]} ${className} rounded-lureo-radius-avatar ${onHero ? 'bg-lureo-gradient-via text-lureo-on-emphasis' : 'bg-lureo-secondary text-lureo-on-secondary'} flex items-center justify-center font-semibold`}>
        {initial}
      </div>
    );
  }

  if (uiImageAvatarUrl) {
    // Use the new AvatarImage component for UI Images
    return (
      <AvatarImage
        imageUrl={uiImageAvatarOriginalUrl || avatarUrl}
        imageCroppedUrl={uiImageAvatarCroppedUrl}
        crop={cropData}
        size={size}
        className={className}
        showFallback={false}
      />
    );
  }

  if (shouldApplyCrop) {
    return (
      <div ref={containerRef} className={`${sizeClasses[size]} ${className} overflow-hidden rounded-lureo-radius-avatar relative`}>
        <img
          src={shownUrl(avatarUrl)}
          alt="User Avatar"
          className="absolute inset-0 w-full h-full object-contain"
          style={{
            transform: `translate(${(legacyCropData?.x || 0) * containerWidth}px, ${(legacyCropData?.y || 0) * containerWidth}px) scale(${(legacyCropData as any)?.scale || 1})`,
            transformOrigin: 'center'
          }}
        />
      </div>
    );
  }

  // Default avatar without crop
  return (
    <RadixAvatar className={`${sizeClasses[size]} ${className}`}>
      <RadixAvatarImage src={shownUrl(avatarUrl)} alt="User Avatar" />
      <AvatarFallback>
        {getUserInitial(user)}
      </AvatarFallback>
    </RadixAvatar>
  );
}

// Default export for backward compatibility
export default Avatar;
